import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { Box, BoxProps, chakra } from '@chakra-ui/react';

import { ItemTypes } from './types';
import { ContentComponent } from '../shared/ContentComponent';

export interface ContentBoxProps extends BoxProps {
  id: any;
  left?: number;
  top?: number;
  type: 'text' | 'image' | 'audio';
  src: string;
  allowWrap?: boolean;
  align?: 'left' | 'right' | 'center' | 'justify';
  textAlign?: 'left' | 'right' | 'center' | 'justify';
  onKeyboardSelect?: () => void;
  isKeyboardSelected?: boolean;
  isKeyboardDisabled?: boolean;
}

const ContentBox: React.FC<ContentBoxProps> = ({
  id,
  left,
  top,
  type,
  src,
  w,
  minW,
  maxW,
  onKeyboardSelect,
  isKeyboardSelected,
  isKeyboardDisabled,
  ...rest
}) => {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const elementRef = useRef<HTMLDivElement | null>(null);

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: ItemTypes.ContentBox,
      item: () => ({
        id,
        left,
        top,
        width,
        height,
        el: elementRef.current,
        type,
        src,
      }),
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [id, left, top, elementRef.current],
  );

  const attach = useCallback(
    (el) => {
      drag(el);
      elementRef.current = el;
    },
    [drag],
  );

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      setWidth(elementRef.current.offsetWidth);
      setHeight(elementRef.current.offsetHeight);
    }
  }, [elementRef]);

  return (
    <chakra.div
      opacity={isDragging ? 0 : undefined}
      color="blue.500"
      position={left != null || top != null ? 'absolute' : 'relative'}
      cursor={isKeyboardDisabled ? 'default' : 'move'}
      left={left}
      top={top}
      ref={attach}
      w={w}
      minW={minW}
      maxW={maxW}
      tabIndex={onKeyboardSelect && !isKeyboardDisabled ? 0 : undefined}
      role={onKeyboardSelect ? 'button' : undefined}
      aria-pressed={onKeyboardSelect ? isKeyboardSelected : undefined}
      background={isKeyboardSelected ? 'rgba(34, 86, 255, 0.1)' : undefined}
      boxShadow={isKeyboardSelected ? '0 0 0 3px #2256ff' : undefined}
      borderRadius="8px"
      sx={{
        '&:focus-visible': {
          outline: '3px solid var(--stemi-focus-color)',
          outlineOffset: '3px',
          borderRadius: '8px',
        },
      }}
      onKeyDown={onKeyboardSelect ? (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onKeyboardSelect();
        }
      } : undefined}
    >
      <ContentBoxPreview
        type={type}
        src={src}
        w={w}
        minW={minW}
        maxW={maxW}
        {...rest}
      />
    </chakra.div>
  );
};

const ContentBoxPreview: React.FC<Omit<ContentBoxProps, 'id'>> = ({
  type,
  src,
  fontSize,
  allowWrap,
  textAlign = 'center',
  align = 'justify',
  ...rest
}) => {
  const textCanWrap = type === 'text' && allowWrap;

  return (
    <Box
      color="blue.500"
      borderRadius="8px"
      borderColor="blue.500"
      backgroundColor="white"
      border="1px"
      fontWeight="bold"
      fontSize={fontSize ?? 'sm'}
      flex="1"
      px={2}
      py={type === 'image' ? 2 : 1}
      w={type === 'image' ? '100px' : 'fit-content'}
      h={type === 'image' ? '100px' : 'fit-content'}
      minW={type === 'text' && !textCanWrap ? 'min-content' : undefined}
      maxW={textCanWrap ? '180px' : undefined}
      alignItems={type === 'image' ? 'center' : undefined}
      justifyItems={type === 'image' ? 'center' : undefined}
      transition="all 250ms ease-in-out"
      boxShadow="md"
      {...rest}
    >
      {ContentComponent({
        type,
        src,
        align,
        textAlign,
        fontSize,
        ...(type === 'text' && {
          whiteSpace: textCanWrap ? ('normal' as const) : ('nowrap' as const),
          wordBreak: 'normal' as const,
        }),
      })}
    </Box>
  );
};

export { ContentBox, ContentBoxPreview };
export default ContentBox;
